import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import { COLLECTIONS, db, upsertUsersRootProfile } from '../../utils/firebase';
import { lookupJoinCode } from '../../utils/joinCode';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import DropdownButton from '../../components/ui/DropdownButton';
import Button from '../../components/ui/Button';
import PrivacyPolicyDialog from '../../components/ui/PrivacyPolicyDialog';
import SeeIcon from '../../components/icons/SeeIcon';
import HideIcon from '../../components/icons/HideIcon';
import GoogleIcon from '../../components/icons/GoogleIcon';
import MicrosoftIcon from '../../components/icons/MicrosoftIcon';

interface LocationState {
  email?: string;
  displayName?: string;
  loginType?: 'google' | 'microsoft';
  isSocialLogin?: boolean;
  uid?: string;
  name?: string;
  from?: { pathname: string };
}

interface ClassData {
  classId: string;
  grade: string;
  class: string;
  schoolId: string;
  [key: string]: any;
}

interface SubjectData {
  subjectId: string;
  schoolId: string;
  [key: string]: any;
}

// 학급 코드 데이터
interface ClassCodeData {
  grade: string;
  class: string;
  schoolId: string;
  classId: string;
  code: string;
  [key: string]: any;
}

interface SubjectDocData {
  schoolId: string;
  [key: string]: any;
}

const StudentRegister: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const { loginWithGoogle, loginWithMicrosoft } = useAuth();
  const state = location.state as LocationState;

  // 회원가입 방식 선택: null(초기), 'local'(일반), 'social'(소셜)
  const [signupType, setSignupType] = useState<'local' | 'social' | null>(
    state?.isSocialLogin ? 'social' : null
  );

  // location.state가 변경될 때 signupType 업데이트
  useEffect(() => {
    if (state?.isSocialLogin && signupType !== 'social') {
      setSignupType('social');
    }
  }, [state?.isSocialLogin]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [formData, setFormData] = useState({
    email: state?.email || '',
    name: state?.name || '',
    password: '',
    confirmPassword: '',
    classCode: '',
    subjectCode: '',
    grade: '',
    class: '',
    number: ''
  });

  const [classCodeStatus, setClassCodeStatus] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  }>({ message: '', type: 'info' });

  const [subjectCodeStatus, setSubjectCodeStatus] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  }>({ message: '', type: 'info' });

  // 개인정보 수집 동의 및 만 14세 미만 보호자 동의 (학교운영위원회 심의·법령 준수)
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeGuardian, setAgreeGuardian] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);

  // 학년 옵션 (1-6학년)
  const gradeOptions = Array.from({ length: 6 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1}학년`
  }));

  // 반 옵션 (1-10반)
  const classOptions = Array.from({ length: 10 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1}반`
  }));

  // 번호 옵션 (1-40번)
  const numberOptions = Array.from({ length: 40 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1}번`
  }));

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // 이메일 유효성 검사
    if (name === 'email') {
      if (!value) {
        setEmailError('');
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          setEmailError('이메일을 확인해 주세요');
        } else {
          setEmailError('');
        }
      }
    }

    // 비밀번호 유효성 검사
    if (name === 'password') {
      if (!value) {
        setPasswordError('');
      } else if (value.length < 6) {
        setPasswordError('비밀번호는 최소 6자 이상이어야 합니다.');
      } else {
        setPasswordError('');
      }
      // 비밀번호 확인도 다시 검사
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setConfirmPasswordError('비밀번호가 일치하지 않습니다.');
      } else if (formData.confirmPassword) {
        setConfirmPasswordError('');
      }
    }

    // 비밀번호 확인 유효성 검사
    if (name === 'confirmPassword') {
      if (!value) {
        setConfirmPasswordError('');
      } else if (value !== formData.password) {
        setConfirmPasswordError('비밀번호가 일치하지 않습니다.');
      } else {
        setConfirmPasswordError('');
      }
    }

    // 학급 코드가 입력된 경우 해당 학급의 정보를 조회
    if (name === 'classCode') {
      if (!value) {
        setClassCodeStatus({ message: '', type: 'info' });
        return;
      }

      try {
        const found = await lookupJoinCode('class', value);
        if (found?.schoolId) {
          setFormData(prev => ({
            ...prev,
            grade: String(found.grade ?? ''),
            class: String(found.class ?? ''),
            schoolId: found.schoolId || ''
          }));
          setClassCodeStatus({ 
            message: `${found.grade}학년 ${found.class}반 정보를 불러왔습니다.`, 
            type: 'success' 
          });
        } else {
          setClassCodeStatus({ 
            message: '존재하지 않는 학급 코드입니다.', 
            type: 'error' 
          });
        }
      } catch (error) {
        console.error('학급 정보 조회 오류:', error);
        setClassCodeStatus({ 
          message: '학급 정보 조회 중 오류가 발생했습니다.', 
          type: 'error' 
        });
      }
    }

    // 교과 코드가 입력된 경우 해당 교과의 정보를 조회
    if (name === 'subjectCode') {
      if (!value) {
        setSubjectCodeStatus({ message: '', type: 'info' });
        return;
      }

      try {
        const found = await lookupJoinCode('subject', value);
        if (found?.schoolId) {
          setFormData(prev => ({
            ...prev,
            schoolId: found.schoolId || ''
          }));
          setSubjectCodeStatus({ 
            message: '유효한 교과 코드입니다.', 
            type: 'success' 
          });
        } else {
          setSubjectCodeStatus({ 
            message: '존재하지 않는 교과 코드입니다.', 
            type: 'error' 
          });
        }
      } catch (error) {
        console.error('교과 정보 조회 오류:', error);
        setSubjectCodeStatus({ 
          message: '교과 정보 조회 중 오류가 발생했습니다.', 
          type: 'error' 
        });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // 학급 코드나 교과 코드 중 하나는 필수
      if (!formData.classCode && !formData.subjectCode) {
        throw new Error('학급 코드 또는 교과 코드를 입력해주세요.');
      }

      let classData: ClassData | null = null;
      let subjectData: SubjectData | null = null;
      let newSchoolId = '';

      // 학급 코드가 입력된 경우
      if (formData.classCode) {
        const found = await lookupJoinCode('class', formData.classCode);
        if (found?.schoolId && found.classId) {
          classData = {
            classId: found.classId,
            grade: String(found.grade ?? ''),
            class: String(found.class ?? ''),
            schoolId: found.schoolId
          };
          newSchoolId = found.schoolId;
          setFormData(prev => ({
            ...prev,
            grade: String(found.grade ?? ''),
            class: String(found.class ?? '')
          }));
        }
      }

      if (formData.subjectCode) {
        const found = await lookupJoinCode('subject', formData.subjectCode);
        if (found?.schoolId && found.subjectId) {
          subjectData = {
            subjectId: found.subjectId,
            schoolId: found.schoolId
          };
          newSchoolId = found.schoolId;
        }
      }

      // 학급이나 교과 중 하나라도 존재하지 않는 경우
      if ((formData.classCode && !classData) || (formData.subjectCode && !subjectData)) {
        throw new Error('잘못된 학급 코드 또는 교과 코드입니다.');
      }

      let user;
      
      // 디버깅을 위한 로그 추가
      console.log('현재 state:', state);
      console.log('현재 auth.currentUser:', auth.currentUser);
      
      // 소셜 로그인인 경우
      if (state?.isSocialLogin) {
        console.log('소셜 로그인 감지됨');
        console.log('auth.currentUser:', auth.currentUser);
        console.log('state.uid:', state.uid);
        
        if (!state.uid) {
          throw new Error('소셜 로그인 사용자 ID를 찾을 수 없습니다.');
        }
        
        // state에서 정보 사용
        user = {
          uid: state.uid,
          email: state.email,
          displayName: state.name,
          providerData: [{ providerId: state.loginType || 'google' }]
        };
        console.log('소셜 로그인 사용자:', user);
      } else {
        console.log('일반 로그인 감지됨');
        // 일반 이메일/비밀번호 로그인인 경우
        if (!formData.password || !formData.confirmPassword) {
          throw new Error('비밀번호를 입력해주세요.');
        }
        if (formData.password.length < 6) {
          throw new Error('비밀번호는 최소 6자 이상이어야 합니다.');
        }
        if (formData.password !== formData.confirmPassword) {
          throw new Error('비밀번호가 일치하지 않습니다.');
        }
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        user = userCredential.user;
      }

      // 학생 문서 생성
      const studentData = {
        uid: user.uid,
        email: user.email || formData.email,
        name: user.displayName || formData.name,
        role: 'student',
        schoolId: newSchoolId,
        grade: classData?.grade || formData.grade || '',
        class: classData?.class || formData.class || '',
        number: formData.number || '',
        stamps: [],
        loginType: user.providerData[0]?.providerId || 'email',
        points: 0,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        assignedRoles: [],
        roles: [],
        classId: classData?.classId || '',
        subjectId: subjectData?.subjectId || '',
        status: 'pending'
      };

      // student_requests 컬렉션에 승인 요청 저장
      await setDoc(doc(db, COLLECTIONS.STUDENT_REQUESTS, user.uid), studentData);

      // 교과 코드가 입력된 경우 subject_requests 컬렉션에도 저장
      if (formData.subjectCode && subjectData) {
        const subjectRequestData = {
          ...studentData,
          subjectId: subjectData.subjectId,
          status: 'pending',
          createdAt: new Date(),
          updatedAt: new Date()
        };
        await setDoc(doc(db, COLLECTIONS.STUDENT_REQUESTS, user.uid), subjectRequestData);
      }

      const loginTypeRaw = user.providerData[0]?.providerId || 'password';
      await upsertUsersRootProfile({
        uid: user.uid,
        email: user.email || formData.email,
        name: user.displayName || formData.name,
        role: 'student',
        schoolId: newSchoolId,
        loginType: loginTypeRaw === 'password' ? 'local' : loginTypeRaw,
      });

      // 소셜 로그인인 경우 로그아웃 처리
      if (state?.isSocialLogin) {
        await signOut(auth);
      }

      // 원래 경로 가져오기
      const from = location.state?.from?.pathname || '/';

      // 성공 시 리다이렉트: 원래 경로가 보호된 경로인 경우 기본 페이지로, 아니면 원래 경로로
      if (from.startsWith('/class/') || from.startsWith('/admin/') || from.startsWith('/subject/')) {
        navigate('/class/main/ClassManagementHome', { 
          state: { 
            message: '담임 교사의 승인을 기다리는 중입니다. 승인 후 로그인이 가능합니다.' 
          } 
        });
      } else {
        navigate(from, { 
          state: { 
            message: '담임 교사의 승인을 기다리는 중입니다. 승인 후 로그인이 가능합니다.' 
          } 
        });
      }
    } catch (error: any) {
      console.error('회원가입 오류:', error);
      setError(error.message || '회원가입에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'microsoft') => {
    setError('');
    setLoading(true);

    try {
      const from = location.state?.from?.pathname || '/studentregister';
      
      if (provider === 'google') {
        await loginWithGoogle('student');
      } else {
        await loginWithMicrosoft('student');
      }
    } catch (socialError: any) {
      console.error('소셜 로그인 오류:', socialError);

      if (socialError.code === 'auth/account-exists-with-different-credential') {
        setError('이미 다른 로그인 방식으로 등록된 계정입니다.\n이메일/비밀번호로 로그인하시거나, 다른 소셜 로그인 방식을 시도해주세요.');
      } else if (socialError.code === 'auth/popup-closed-by-user') {
        setError('로그인 창이 닫혔습니다. 다시 시도해주세요.');
      } else if (socialError.code === 'auth/cancelled-popup-request') {
        setError('로그인이 취소되었습니다. 다시 시도해주세요.');
      } else if (socialError.message === 'SOCIAL_SIGNUP_REQUIRED') {
        navigate('/studentregister', { 
          state: { 
            isSocialSignup: true, 
            provider,
            from: location.state?.from
          } 
        });
      } else {
        setError(socialError.message || '소셜 로그인에 실패했습니다. 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return formData.email && 
           (!state?.isSocialLogin ? (formData.password && formData.confirmPassword && formData.password.length >= 6 && formData.password === formData.confirmPassword) : true) &&
           formData.name &&
           (formData.classCode || formData.subjectCode) &&
           formData.grade &&
           formData.class &&
           formData.number &&
           agreePrivacy &&
           agreeGuardian &&
           !emailError &&
           !passwordError &&
           !confirmPasswordError;
  };

  // 회원가입 방식 선택 화면
  if (!signupType) {
    return (
      <div style={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '80px',
        paddingBottom: '80px',
        overflowY: 'auto'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '600px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '40px',
          padding: '0 20px'
        }}>
          {/* 제목 */}
          <div style={{
            textAlign: 'center',
            color: 'black',
            fontSize: '24px',
            fontFamily: 'Pretendard',
            fontWeight: '500',
            lineHeight: '36px',
            wordWrap: 'break-word'
          }}>
            학생 회원가입
          </div>

          {/* 설명 */}
          <div style={{
            textAlign: 'center',
            color: '#989898',
            fontSize: '14px',
            fontFamily: 'Pretendard',
            fontWeight: '400',
            lineHeight: '21px',
            wordWrap: 'break-word'
          }}>
            어떤 방식으로 회원가입하시겠습니까?
          </div>

          {/* 선택 카드들 */}
          <div style={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '32px'
          }}>
            {/* 일반 회원가입 카드 */}
            <div
              onClick={() => setSignupType('local')}
              style={{
                padding: '32px',
                background: 'white',
                borderRadius: '20px',
                border: '1.2px solid #F0F0F0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: '#FFF4ED',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '40px'
              }}>
                ✉️
              </div>
              <div style={{
                color: 'black',
                fontSize: '20px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '30px'
              }}>
                일반 회원가입
              </div>
              <div style={{
                color: '#989898',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '400',
                lineHeight: '21px'
              }}>
                이메일과 비밀번호로<br />회원가입합니다
              </div>
            </div>

            {/* 소셜 회원가입 카드 */}
            <div
              onClick={() => setSignupType('social')}
              style={{
                padding: '32px',
                background: 'white',
                borderRadius: '20px',
                border: '1.2px solid #F0F0F0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: '#F0F9FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '40px'
              }}>
                🔐
              </div>
              <div style={{
                color: 'black',
                fontSize: '20px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '30px'
              }}>
                소셜 회원가입
              </div>
              <div style={{
                color: '#989898',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '400',
                lineHeight: '21px'
              }}>
                Google 또는 Microsoft<br />계정으로 회원가입합니다
              </div>
            </div>
          </div>

          {/* 뒤로 가기 */}
          <div
            onClick={() => navigate(-1)}
            style={{
              cursor: 'pointer',
              color: '#989898',
              fontSize: '14px',
              fontFamily: 'Pretendard',
              fontWeight: '400',
              lineHeight: '21px',
              textDecoration: 'underline'
            }}
          >
            뒤로 가기
          </div>
        </div>
      </div>
    );
  }

  // 회원가입 폼 화면
  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingTop: '36.5px',
      paddingBottom: '40px',
      overflowY: 'auto'
    }}>
        <div style={{
          width: '410px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          gap: '40px'
        }}>
          {/* 제목 */}
          <div style={{
            alignSelf: 'stretch',
            textAlign: 'center',
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
            color: 'black',
            fontSize: '24px',
            fontFamily: 'Pretendard',
            fontWeight: '500',
            lineHeight: '36px',
            wordWrap: 'break-word'
          }}>
            학생 회원가입
          </div>

          {/* 소셜 로그인 섹션 - 소셜 회원가입 선택 시에만 표시 */}
          {signupType === 'social' && !state?.isSocialLogin && (
            <div style={{
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '100%',
                textAlign: 'center',
                color: '#989898',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: 500,
                lineHeight: '21px'
              }}>
                다음 계정으로 계속하기
              </div>

              <div style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <Button
                  variant="line"
                  size="large"
                  width="100%"
                  padding="16px 20px"
                  iconSize={18}
                  leftIcon={<GoogleIcon width={18} height={18} />}
                  disabled={loading}
                  onClick={() => handleSocialLogin('google')}
                >
                  Google로 계속하기
                </Button>

                <Button
                  variant="line"
                  size="large"
                  width="100%"
                  padding="16px 20px"
                  iconSize={18}
                  leftIcon={<MicrosoftIcon width={18} height={18} />}
                  disabled={loading}
                  onClick={() => handleSocialLogin('microsoft')}
                >
                  Microsoft로 계속하기
                </Button>
              </div>
            </div>
          )}

          {/* 기본 정보 섹션 - 일반 회원가입 또는 소셜 로그인 완료 후 */}
          {(signupType === 'local' || state?.isSocialLogin) && (
            <>
              <div style={{
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '16px'
              }}>
                <div style={{
                  alignSelf: 'stretch',
                  height: '24px',
                  textAlign: 'center',
                  justifyContent: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  color: 'black',
                  fontSize: '16px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '24px',
                  wordWrap: 'break-word'
                }}>
                  기본 정보를 입력해주세요
                </div>

                <div style={{
                  alignSelf: 'stretch',
                  paddingLeft: '28px',
                  paddingRight: '28px',
                  paddingTop: '24px',
                  paddingBottom: '24px',
                  background: '#F8F8F8',
                  borderRadius: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                {/* 이메일 입력 필드 */}
                <div style={{
                  alignSelf: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    alignSelf: 'stretch',
                    height: '48px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    outline: '1.2px solid #F0F0F0',
                    outlineOffset: '-1.2px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="이메일을 입력해 주세요"
                      disabled={loading || !!state?.isSocialLogin}
                      style={{
                        flex: '1 1 0',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: formData.email ? '#161616' : '#989898',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '400',
                        lineHeight: '19.5px'
                      }}
                    />
                  </div>
                  {emailError && (
                    <div style={{
                      alignSelf: 'stretch',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{
                        color: '#FF3838',
                        fontSize: '12px',
                        fontFamily: 'Pretendard',
                        fontWeight: '500',
                        lineHeight: '18px',
                        wordWrap: 'break-word'
                      }}>
                        {emailError}
                      </div>
                    </div>
                  )}
                </div>

                {/* 비밀번호 입력 필드 */}
                {!state?.isSocialLogin && (
                  <>
                    <div style={{
                      alignSelf: 'stretch',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{
                        alignSelf: 'stretch',
                        height: '48px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        background: 'white',
                        borderRadius: '8px',
                        outline: '1.2px solid #F0F0F0',
                        outlineOffset: '-1.2px',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="비밀번호를 입력해 주세요"
                          disabled={loading}
                          style={{
                            flex: '1 1 0',
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            color: formData.password ? '#161616' : '#989898',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '19.5px'
                          }}
                        />
                        <div
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start'
                          }}
                        >
                          {showPassword ? (
                            <SeeIcon width={24} height={24} fill="#161616" />
                          ) : (
                            <HideIcon width={24} height={24} fill="#989898" />
                          )}
                        </div>
                      </div>
                      {passwordError && (
                        <div style={{
                          alignSelf: 'stretch',
                          paddingTop: '8px',
                          paddingBottom: '8px',
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <div style={{
                            color: '#FF3838',
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '18px',
                            wordWrap: 'break-word'
                          }}>
                            {passwordError}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 비밀번호 확인 입력 필드 */}
                    <div style={{
                      alignSelf: 'stretch',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{
                        alignSelf: 'stretch',
                        height: '48px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        background: 'white',
                        borderRadius: '8px',
                        outline: '1.2px solid #F0F0F0',
                        outlineOffset: '-1.2px',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          placeholder="비밀번호를 한번 더 입력해 주세요"
                          disabled={loading}
                          style={{
                            flex: '1 1 0',
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            color: formData.confirmPassword ? '#161616' : '#989898',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '19.5px'
                          }}
                        />
                        <div
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start'
                          }}
                        >
                          {showConfirmPassword ? (
                            <SeeIcon width={24} height={24} fill="#161616" />
                          ) : (
                            <HideIcon width={24} height={24} fill="#989898" />
                          )}
                        </div>
                      </div>
                      {confirmPasswordError && (
                        <div style={{
                          alignSelf: 'stretch',
                          paddingTop: '8px',
                          paddingBottom: '8px',
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <div style={{
                            color: '#FF3838',
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '18px',
                            wordWrap: 'break-word'
                          }}>
                            {confirmPasswordError}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* 이름 입력 필드 */}
                <div style={{
                  alignSelf: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    alignSelf: 'stretch',
                    height: '48px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    outline: '1.2px solid #F0F0F0',
                    outlineOffset: '-1.2px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="이름을 입력해 주세요"
                      disabled={loading || !!state?.isSocialLogin}
                      style={{
                        flex: '1 1 0',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: formData.name ? '#161616' : '#989898',
                        fontSize: '13px',
                        fontFamily: 'Pretendard',
                        fontWeight: '400',
                        lineHeight: '19.5px'
                      }}
                    />
                  </div>
                </div>

                {/* 학년/반/번호 선택 */}
                <div style={{
                  alignSelf: 'stretch',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: '9px'
                }}>
                  <DropdownButton
                    options={gradeOptions}
                    value={formData.grade}
                    onChange={(value) => setFormData(prev => ({ ...prev, grade: value }))}
                    placeholder="학년"
                    disabled={loading || !!formData.classCode}
                    width="calc(33.33% - 6px)"
                    size="small"
                  />
                  <DropdownButton
                    options={classOptions}
                    value={formData.class}
                    onChange={(value) => setFormData(prev => ({ ...prev, class: value }))}
                    placeholder="반"
                    disabled={loading || !!formData.classCode}
                    width="calc(33.33% - 6px)"
                    size="small"
                  />
                  <DropdownButton
                    options={numberOptions}
                    value={formData.number}
                    onChange={(value) => setFormData(prev => ({ ...prev, number: value }))}
                    placeholder="번호"
                    disabled={loading}
                    width="calc(33.33% - 6px)"
                    size="small"
                  />
                  </div>
                </div>
              </div>
              </div>
              </>
          )}

          {/* 코드 입력 섹션 - 소셜 로그인 버튼 표시 중일 때도 표시 */}
          {signupType && (
            <div style={{
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: '40px'
            }}>
              <div style={{
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '16px'
              }}>
                <div style={{
                  alignSelf: 'stretch',
                  height: '53px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <div style={{
                    alignSelf: 'stretch',
                    flex: '1 1 0',
                    textAlign: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    color: 'black',
                    fontSize: '16px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '24px',
                    wordWrap: 'break-word'
                  }}>
                    코드를 입력해주세요
                  </div>
                  <div style={{
                    alignSelf: 'stretch',
                    flex: '1 1 0',
                    textAlign: 'center',
                    color: '#989898',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '21px',
                    wordWrap: 'break-word'
                  }}>
                    학급 코드 혹은 교과 코드 중 하나는 필수입니다.
                  </div>
                </div>

                <div style={{
                  alignSelf: 'stretch',
                  paddingLeft: '28px',
                  paddingRight: '28px',
                  paddingTop: '24px',
                  paddingBottom: '24px',
                  background: '#F8F8F8',
                  borderRadius: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    alignSelf: 'stretch',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    {/* 학급 코드 입력 필드 */}
                    <div style={{
                      alignSelf: 'stretch',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{
                        alignSelf: 'stretch',
                        height: '48px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        background: 'white',
                        borderRadius: '8px',
                        outline: '1.2px solid #F0F0F0',
                        outlineOffset: '-1.2px',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <input
                          type="text"
                          name="classCode"
                          value={formData.classCode}
                          onChange={handleInputChange}
                          placeholder="학급 코드"
                          disabled={loading}
                          style={{
                            flex: '1 1 0',
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            color: formData.classCode ? '#161616' : '#989898',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '19.5px'
                          }}
                        />
                      </div>
                      {classCodeStatus.message && (
                        <div style={{
                          alignSelf: 'stretch',
                          paddingTop: '8px',
                          paddingBottom: '8px',
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <div style={{
                            color: classCodeStatus.type === 'error' ? '#FF3838' : '#161616',
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '18px',
                            wordWrap: 'break-word'
                          }}>
                            {classCodeStatus.message}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 교과 코드 입력 필드 */}
                    <div style={{
                      alignSelf: 'stretch',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{
                        alignSelf: 'stretch',
                        height: '48px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        background: 'white',
                        borderRadius: '8px',
                        outline: '1.2px solid #F0F0F0',
                        outlineOffset: '-1.2px',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <input
                          type="text"
                          name="subjectCode"
                          value={formData.subjectCode}
                          onChange={handleInputChange}
                          placeholder="교과 코드"
                          disabled={loading}
                          style={{
                            flex: '1 1 0',
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            color: formData.subjectCode ? '#161616' : '#989898',
                            fontSize: '13px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '19.5px'
                          }}
                        />
                      </div>
                      {subjectCodeStatus.message && (
                        <div style={{
                          alignSelf: 'stretch',
                          paddingTop: '8px',
                          paddingBottom: '8px',
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <div style={{
                            color: subjectCodeStatus.type === 'error' ? '#FF3838' : '#161616',
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '18px',
                            wordWrap: 'break-word'
                          }}>
                            {subjectCodeStatus.message}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 에러 메시지와 등록 버튼 - 일반 회원가입 또는 소셜 로그인 완료 후에만 표시 */}
          {(signupType === 'local' || (signupType === 'social' && state?.isSocialLogin)) && (
            <>
              {/* 개인정보 수집 동의 및 만 14세 미만 보호자 동의 (학교운영위원회 심의·법령 준수) */}
              <div style={{
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    color: 'var(--Color-text-normal, #161616)'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={agreePrivacy}
                    onChange={(e) => setAgreePrivacy(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    disabled={loading}
                  />
                  <span>
                    (필수){' '}
                    <span
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowPrivacyDialog(true);
                      }}
                      style={{
                        color: 'var(--Color-fill-brand-color, #FF6F0F)',
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                    >
                      개인정보 수집 및 이용에 동의합니다.
                    </span>
                  </span>
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    color: 'var(--Color-text-normal, #161616)'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={agreeGuardian}
                    onChange={(e) => setAgreeGuardian(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    disabled={loading}
                  />
                  <span>(필수) (만 14세 미만인 경우) 학교 가정통신문 등을 통해 보호자 동의를 완료하였습니다.</span>
                </label>
                <PrivacyPolicyDialog
                  isOpen={showPrivacyDialog}
                  onClose={() => setShowPrivacyDialog(false)}
                  onAgree={() => setAgreePrivacy(true)}
                />
              </div>

              {/* 에러 메시지 */}
              {error && (
                <div style={{
                  alignSelf: 'stretch',
                  padding: '12px 16px',
                  background: '#FFE5E5',
                  borderRadius: '8px',
                  color: '#FF3838',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '400',
                  lineHeight: '21px'
                }}>
                  {error}
                </div>
              )}

              {/* 등록 버튼 */}
              <div style={{ width: '100%' }}>
                <Button
                  width="100%"
                  height="48px"
                  padding="16px 20px"
                  variant="submit"
                  size="large"
                  disabled={!isFormValid() || loading}
                  onClick={(e) => {
                    e?.preventDefault();
                    handleSubmit(e as any);
                  }}
                >
                  {loading ? '등록 중...' : '등록'}
                </Button>
              </div>
            </>
          )}
        </div>
    </div>
  );
};

export default StudentRegister; 